import { Component, EventEmitter, Input, Output } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { Subject, takeUntil } from 'rxjs';
import { WindowService } from '../../../core/services/window.service';
import { MessagingService } from '../../../core/services/messaging.service';
import { DataService } from '../../../core/services/data.service';
import _ from 'lodash';

import { FormsModule } from '@angular/forms';
import { OrganizationService } from '../../../core/services/organization.service';
import { MultipleMessageComponent } from '../organization/multiple-message/multiple-message.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-ad-hoc-message-window',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './ad-hoc-message-window.component.html',
  styleUrl: './ad-hoc-message-window.component.css',
})
export class AdHocMessageWindowComponent {
  @Input() locations: number[][] | null = [];
  @Input() travelersonly: boolean = false;
  @Output() openGeofenceCreator = new EventEmitter<void>();

  usersFound: any[] = [];
  facilitiesFound: any[] = [];
  remotesFound: any[] = [];
  travelersFound: any[] = [];

  showUsersTab = false;
  showRemotesTab = false;
  showTravelersTab = false;
  showFacilitiesTab = false;

  private adhocleafletmap!: L.Map;
  private peopleClusters!: L.MarkerClusterGroup;
  private destroy$ = new Subject<void>();
  users: any[] = [];

  constructor(
    private windowService: WindowService,
    private messagingService: MessagingService,
    private dataService: DataService,
    private organizationService: OrganizationService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.organizationService.getOrgUsers().subscribe((users) => {
      this.users = users?.rows ?? []; // Store users in a property
      this.initMap();
      this.getUsersInGeofence();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- Tabs ----------------
  toggleUsersTab() {
    const wasOpen = this.showUsersTab;
    this.resetTabs();
    // this.showUsersTab = !this.showUsersTab;
    this.showUsersTab = !wasOpen;
  }
  toggleRemotesTab() {
    this.resetTabs();
    this.showRemotesTab = !this.showRemotesTab;
  }
  toggleTravelersTab() {
    this.resetTabs();
    this.showTravelersTab = !this.showTravelersTab;
  }
  toggleFacilitiesTab() {
    this.resetTabs();
    this.showFacilitiesTab = !this.showFacilitiesTab;
  }
  private resetTabs() {
    this.showUsersTab =
      this.showRemotesTab =
      this.showTravelersTab =
      this.showFacilitiesTab =
        false;
  }

  // ---------------- Map ----------------

  private initMap() {
    this.adhocleafletmap = L.map('adhocleafletmap');

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
      this.adhocleafletmap
    );

    this.peopleClusters = L.markerClusterGroup();
    this.adhocleafletmap.addLayer(this.peopleClusters);

    if (this.locations && this.locations.length > 0) {
      // Ensure each location has at least 2 numbers and cast to LatLngTuple
      const latlngs: L.LatLngTuple[] = this.locations
        .filter((loc: number[]) => loc.length >= 2)
        .map((loc: number[]) => [loc[1], loc[0]] as L.LatLngTuple);

      const polygon = L.polygon(latlngs).addTo(this.adhocleafletmap);

      this.adhocleafletmap.fitBounds(polygon.getBounds(), {
        padding: [50, 50],
      });
    } else {
      // Default view
      this.adhocleafletmap.setView([51.505, -0.09], 4);
    }
  }

  private getUsersInGeofence() {
    const geofence = this.makeGeo(this.locations);

    this.dataService
      .getUserGeofence(geofence)
      ?.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results: any) => {
          results.rows.forEach((user: any) => {
            const userId = parseInt(user.id, 10);

            // Find this user in the full users list
            const fullUser = this.users.find((u) => u.id === user.id);

            // Extract company name from fullUser
            const companyName =
              fullUser?.UserOrganizations?.[0]?.Organization?.name || '';

            // Push into usersFound
            this.usersFound.push({ userId, name: user.name, companyName });

            // Get user's lat/lon
            const latlon = this.getUserLatLon(user);
            if (latlon) {
              const marker = L.marker([latlon.lat, latlon.lon]);
              marker.bindPopup(`${user.name} | ${companyName}`);
              this.peopleClusters.addLayer(marker);
            }
          });
          // Sort usersFound by name
          this.usersFound = _.orderBy(this.usersFound, ['name'], ['asc']);
        },
        error: (err: any) => console.error(err),
      });
  }

  private getUserLatLon(user: any) {
    if (user.Devices?.length > 0) {
      return {
        lat: user.Devices[0].Locations[0].position.coordinates[1],
        lon: user.Devices[0].Locations[0].position.coordinates[0],
      };
    } else if (user.UserAddresses?.length > 0) {
      return {
        lat: user.UserAddresses[0].position.coordinates[1],
        lon: user.UserAddresses[0].position.coordinates[0],
      };
    }
    return null;
  }

  private makeGeo(locations: number[][] | null) {
    const coords = locations?.map((loc: number[]) => [loc[0], loc[1]]);

    return {
      type: 'Polygon',
      coordinates: [coords], // wrap in an extra array as GeoJSON Polygon expects
    };
  }

  // ---------------- Messaging ----------------
  sendMessageToAllUsers() {
    const data: any = {
      items: [],
      isNewFormat: true,
    };
    this.usersFound.forEach((user) => {
      data.items.push({
        userId: user.userId,
        name: user.name,
        companyName: user.companyName,
      });
    });
    const modalRef = this.modalService.open(MultipleMessageComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.title = 'Send Message';
    modalRef.componentInstance.userInfo = null;
    modalRef.componentInstance.showCancel = true;
    modalRef.componentInstance.users = this.users;
    return modalRef.result;
  
  }

  // ---------------- CSV Export ----------------
  exportUserCSV() {
    const rows: string[][] = [['Name', 'Company']];
    this.usersFound.forEach((u) => rows.push([u.name, u.companyName]));

    let csvContent = 'data:text/csv;charset=utf-8,';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\r\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'user_info.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ---------------- Format check ----------------
  isNewFormat() {
    return this.messagingService.data?.orgInfoInPapi?.id > 0;
  }

  closeModal() {
    this.openGeofenceCreator.emit();
  }

}
